import { Map } from "common/utils/Map";
import { Route, Params, Fn } from "../types";
import { GeoCoords } from "common/types";
import { DEFAULT_SERVICE_POINT_DISPLAY_RADIUS } from "common/constants/servicePointDisplayRadius";

import { lineString } from '@turf/helpers';
import getGUID from "common/utils/getGUID";
import { getWaypointIcon } from "entities/map";
import { intl } from 'i18n/Translations';
import { getInfoWindow } from "../utils";

export const AVERAGE_OVERVIEW_PATH_ITEMS = 300;

export class HereRoute implements Route {
  private _map: Map;
  private _id: string;
  private _routeLine?: any;
  private _group?: any;
  private _waypointsGroup?: any;
  private _infoWindow?: any;
  public sections: Params['sections'];
  public routePoints: Params['routePoints'];
  public polylines?: string[];
  public time: Params["time"];
  public startAddress: Params["startAddress"];
  public endAddress: Params["endAddress"];
  public distance: Params["distance"];
  public bounds: Params["bounds"];
  public legs: Params["legs"];
  public overviewPath: Params["overviewPath"] = [];
  public overviewPolyline: Params["overviewPolyline"];
  public multilineString: Params["multilineString"];
  public turfLinestring: Route['turfLinestring'] = lineString([[0, 0], [1, 1]]);
  private _polylineOptions = {
    strokeColor: "#008DC9",
    lineWidth: 5,
  };

  constructor(map: Map, params: Params) {
    const {
      time,
      startAddress,
      endAddress,
      distance,
      bounds,
      legs,
      multilineString,
      routePoints,
      sections
    } = params;

    this._map = map;
    this._id = getGUID();
    this.time = time;
    this.startAddress = startAddress;
    this.endAddress = endAddress;
    this.distance = distance;
    this.bounds = bounds;
    this.legs = legs;
    this.multilineString = multilineString;
    this.routePoints = routePoints;
    this.sections = sections;
    this._initTurfLinestring();
    this._initGroup();
    this._initRouteLine();
    this._addRouteLineToGroup();
    this._initOverviewPath();
  }

  public get id() {
    return this._id;
  }

  _initTurfLinestring() {
    const { coordinates: sectionsCoordinates } = this.multilineString.toGeoJSON() as { coordinates: [number, number, number][][] };
    const overviewPathCoordinates = sectionsCoordinates.reduce((acc, sectionCoordinates) => ([
      ...acc,
      ...sectionCoordinates
    ]), [] as [number, number, number][]);
    this.turfLinestring = lineString(overviewPathCoordinates);
  }

  _initOverviewPath() {
    const overviewPath: any[] = [];

    this._routeLine
      .getGeometry()
      .getGeometries()
      .forEach((geometry: any) => {
        geometry.eachLatLngAlt((lat: number, lng: number) => {
          overviewPath.push({ lat, lng });
        }, 0);
      });

    const shift = Math.round(overviewPath.length / AVERAGE_OVERVIEW_PATH_ITEMS);

    this.overviewPath = overviewPath.reduce((acc, item, index) => {
      if (
        index === 0 ||
        index % shift === 0 ||
        index === overviewPath.length - 1
      ) {
        acc.push(item);
      }

      return acc;
    }, [] as any[]);
  }

  _getOverviewPathMidpoint() {
    return this.overviewPath[Math.round(this.overviewPath.length / 2)];
  }

  _initRouteLine() {
    this._routeLine = new window.H.map.Polyline(this.multilineString, {
      style: {
        ...this._polylineOptions,
      },
      zIndex: 0,
    });
  }

  _initGroup() {
    this._group = new window.H.map.Group();
  }

  _addRouteLineToGroup() {
    if (this._group) {
      this._group.addObject(this._routeLine);
    }
  }

  _addWaypointMarkers() {
    if (this.routePoints) {
      this._waypointsGroup = new window.H.map.Group();
      const routePoints = [
        this.routePoints.firstPoint,
        ...this.routePoints.waypoints,
        this.routePoints.lastPoint
      ];

      routePoints.forEach(({ coordinates }, index) => {
        let name = '';
        
        if (index === 0) {
          name = 'startPoint';
        } else if (index === routePoints.length - 1) {
          name = 'endPoint';
        } else {
          name = `waypoint${index}`;
        }

        this._waypointsGroup.addObject(
          // @ts-ignore
          new window.H.map.Marker({
            lat: Number(coordinates?.lat),
            lng: Number(coordinates?.lng),
          }, {
            icon: new window.H.map.Icon(getWaypointIcon(name), {
              size: {
                w: 32,
                h: 32,
              },
            }),
          })
        );
      });

      this._map.getNativeMap().addObject(this._waypointsGroup);
    }
  }

  _deleteWaypointMarkers() {
    this._map.getNativeMap().removeObject(this._waypointsGroup);
  }

  addInfoWindow() {
    this._infoWindow = new window.H.ui.InfoBubble(
      this._getOverviewPathMidpoint(),
      {
        content: getInfoWindow({
          distance: `${this.distance} ${intl.formatMessage({ id: 'km' })}` ,
          time: this.time,
        }),
      }
    );
    this._map.mapUi.addBubble(this._infoWindow);
  }

  deleteInfoWindow() {
    this._map.mapUi.removeBubble(this._infoWindow);
  }

  selectRoute(isSelected: boolean) {
    this._routeLine.setStyle({
      ...this._polylineOptions,
      strokeColor: isSelected ? "#4FBC46" : this._polylineOptions.strokeColor,
    });

    this._group.setZIndex(isSelected ? 5 : 0);
  }

  show() {
    this._map.getNativeMap().addObject(this._group);
    this._addWaypointMarkers();
  }

  delete() {
    this._map.getNativeMap().removeObject(this._group);
    this._deleteWaypointMarkers();
    this.deleteInfoWindow();
  }

  addListenerClick(fn: Fn) {}

  checkIsLocationOnRoute(
    point: GeoCoords,
    tolerance: number = DEFAULT_SERVICE_POINT_DISPLAY_RADIUS
  ): boolean {
    return false;
  }
}
